import { QualifiedFeature } from "fe-common/graphql";
import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, List, styled } from "@mui/material";
import { DataInput, LevelValue } from "./dataInputTypes";
import { PanelContext, PatientCaseGrouping } from "../context/diagnosticAgent";
import {
  findingUsesLevel,
  panelUsesLevel,
} from "./diagnosticAgent/useDataInputPanels";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { clone, sortBy, memoize } from "lodash";
import { useDiagnosticNavigation } from "./diagnosticAgent/useDiagnosticNavigation";
import { EditablePanelItem } from "./diagnosticAgent/editablePanelItem";
import { KahunPanelSubGroup } from "shared";
import { Suggestion } from "./diagnosticAgent/hooks/useSuggestions";
import { PanelHeader } from "./diagnosticAgent/panel/panelHeader";

//region [[ Styles ]]
const FindingsList = styled("div")(
  ({ enabled, wrap }: { enabled: string; wrap: string }) => ({
    opacity: enabled === "true" ? 1 : 0.4,
    display: wrap === "true" ? "flex" : "inherit",
    flexDirection: "column",
  }),
);

const DataInputPanelEditView = styled("div")({
  display: "flex",
  flex: 1,
  position: "relative",
  flexGrow: 2,
  minHeight: "min-content",
  flexDirection: "column",
  alignItems: "stretch",
  width: "100%",
});

const PanelItemHeader = styled(PanelHeader)({
  "&:hover": {
    backgroundColor: "transparent",
  },
});

const ShowMoreButton = styled(Button)({});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface DataInputPanelEditProps {
  getCaseValue: (
    qf: QualifiedFeature,
    section: PatientCaseGrouping,
  ) => DataInput;
  setCaseValue: (
    qf: QualifiedFeature,
    r: DataInput,
    section: PatientCaseGrouping,
    panelContext: PanelContext,
    log?: boolean,
  ) => void;
  panelContext: PanelContext;
  filterFindings?: QualifiedFeature[];
  suggestions?: Suggestion[];
  /***
   * For medications set the drug feature to we could update it properly
   */
  drugFeature: QualifiedFeature;
}
//endregion [[ Props ]]

// region [[ Functions ]]
//endregion [[ Functions ]]

// region [[ Constants ]]
const blackListFindings = ["FE.034e2c7e-1194-4d91-8f69-b13f407f7759"];
// Start with 50 suggestions for better performance, let the user add another 50 every time
const ITEMS_PAGINATION = 30;
//endregion [[ Constants ]]

/**
 * Displays a single panel and allows it to be edited
 * @param props
 * @constructor
 */
export const DataInputPanelEdit = (props: DataInputPanelEditProps) => {
  const { panelContext, getCaseValue, setCaseValue } = props;

  const { caseGrouping: section, panel } = panelContext;

  const [someAbnormalClicked, setSomeAbnormalClicked] = useState(false);

  // Start with 50 items at first
  const [maxItemsPerPanel, setMaxItemsPerPanel] = useState(ITEMS_PAGINATION);

  const { setRefinePopupProps } = useDiagnosticNavigation();

  let usesLevel = panelUsesLevel(panel);

  const onMakeNormalLabs = useCallback(() => {
    const levelFindings = panel.findings.filter((qf) => findingUsesLevel(qf));
    const positiveNegativeFindings = panel.findings.filter(
      (qf) => !findingUsesLevel(qf),
    );

    const numLevelNull = levelFindings.filter((qf) => {
      const resp = getCaseValue(qf, section);
      return !resp || resp.level === null || resp.level !== LevelValue.NORMAL;
    }).length;

    const numPositiveNegativeNull = positiveNegativeFindings.filter((qf) => {
      const resp = getCaseValue(qf, section);
      return !resp || resp.negate === null || resp.negate === undefined;
    }).length;

    setSomeAbnormalClicked(false);

    evt({
      category: CATEGORY.AGENT_WIZARD,
      action:
        numPositiveNegativeNull || numLevelNull > 0
          ? ACTION.CHECK_ALL_NORMAL
          : ACTION.UNCHECK_ALL_NORMAL,
      label: panel.title,
    });

    levelFindings.forEach((qf) => {
      setCaseValue(
        qf,
        numLevelNull > 0 ? { level: LevelValue.NORMAL } : null,
        section,
        panelContext,
        false,
      );
    });

    positiveNegativeFindings.forEach((qf) => {
      setCaseValue(
        qf,
        numPositiveNegativeNull || numLevelNull > 0 ? { negate: true } : null,
        section,
        panelContext,
        false,
      );
    });
  }, [
    panel,
    panelContext,
    setSomeAbnormalClicked,
    setCaseValue,
    getCaseValue,
    section,
  ]);

  const onMakeNormalProcedures = useCallback(() => {
    setSomeAbnormalClicked(false);

    //const levelFindings = panel.findings.filter((qf) => findingUsesLevel(qf));
    const positiveNegativeFindings = panel.findings.filter(
      (qf) => !findingUsesLevel(qf),
    );

    positiveNegativeFindings.forEach((qf) => {
      if (getCaseValue(qf, section) !== null) {
        setCaseValue(qf, null, section, panelContext, false);
      }
    });

    let first = clone(positiveNegativeFindings[0]);
    if (!first) {
      return;
    }

    first.qualifiers = [];
    const responseQf = getCaseValue(first, section);

    evt({
      category: CATEGORY.AGENT_WIZARD,
      action: responseQf ? ACTION.UNCHECK_ALL_NORMAL : ACTION.CHECK_ALL_NORMAL,
      label: panel.title,
    });

    if (responseQf && responseQf.negate === true) {
      setCaseValue(first, null, section, panelContext, false);
    } else {
      let first = clone(positiveNegativeFindings[0]);
      first.qualifiers = [];
      setCaseValue(first, { negate: true }, section, panelContext, false);
    }
  }, [panel, panelContext, section, setCaseValue, getCaseValue]);

  const onMakeNormal = useCallback(() => {
    if (panelContext.panelCategory.id !== KahunPanelSubGroup.Procedures) {
      onMakeNormalLabs();
    } else {
      onMakeNormalProcedures();
    }
  }, [panelContext, onMakeNormalLabs, onMakeNormalProcedures]);

  const isProcedurePanelAbnormal = useCallback(
    (findings) => {
      const positiveNegativeFindings = findings.filter(
        (qf) => !findingUsesLevel(qf),
      );

      let panelAbnormal = false;

      if (positiveNegativeFindings.length > 0) {
        positiveNegativeFindings.forEach((finding) => {
          const resp = getCaseValue(finding, PatientCaseGrouping.WORKUP);
          if (resp && resp.negate !== true) {
            panelAbnormal = true;
          }
        });
      }

      return panelAbnormal || someAbnormalClicked;
    },
    [getCaseValue, someAbnormalClicked],
  );

  const onSelectAbnormal = useCallback(() => {
    evt({
      category: CATEGORY.AGENT_WIZARD,
      action: ACTION.CLICK_PROCEDURES_SELECT_ABNORMAL,
      label: panel.title,
    });

    const levelFindings = panel.findings.filter((qf) => findingUsesLevel(qf));
    const positiveNegativeFindings = panel.findings.filter(
      (qf) => !findingUsesLevel(qf),
    );

    setSomeAbnormalClicked(!isProcedurePanelAbnormal(positiveNegativeFindings));

    levelFindings.forEach((qf) => {
      if (getCaseValue(qf, section) !== null) {
        setCaseValue(qf, null, section, panelContext, false);
      }
    });

    positiveNegativeFindings.forEach((qf) => {
      if (getCaseValue(qf, section) !== null) {
        //  setCaseValue(qf, null, section);
      }
    });

    let first = clone(positiveNegativeFindings[0]);
    first.qualifiers = [];

    if (getCaseValue(first, section) !== null) {
      setCaseValue(first, null, section, panelContext, false);
    }
  }, [
    panel,
    setSomeAbnormalClicked,
    getCaseValue,
    isProcedurePanelAbnormal,
    panelContext,
    section,
    setCaseValue,
  ]);

  const createRefineFindingHandler = useCallback(
    (qf: QualifiedFeature) => {
      evt({
        category: CATEGORY.AGENT_WIZARD,
        action: ACTION.CLICK_REFINE,
        label: qf.name,
      });

      setRefinePopupProps({
        open: true,
        refineQualifier: qf,
        patientCaseGrouping: section,
      });
    },
    [section, setRefinePopupProps],
  );

  const findings = useMemo(
    () => [...(panel?.customFindings || []), ...(panel?.findings || [])],
    [panel],
  );

  const filterBlackList = useCallback(
    (finding) =>
      blackListFindings.some(
        (blackListFindingId) => blackListFindingId !== finding.id,
      ),
    [],
  );

  let filteredFindings = useMemo(
    () =>
      (props.filterFindings ? props.filterFindings : findings).filter(
        filterBlackList,
      ),
    [props.filterFindings, findings, filterBlackList],
  );

  const getSuggestion = useCallback(
    (qf: QualifiedFeature) => {
      if (!props.suggestions) {
        return;
      }

      if (
        section === PatientCaseGrouping.WORKUP &&
        panel.panelSubgroup.id === KahunPanelSubGroup.Procedures
      ) {
        return props.suggestions.find((suggestion) =>
          suggestion.causedByDisease.some(
            (c) =>
              c.suggestion.qualifiers[0].value.id === qf.qualifiers[0].value.id,
          ),
        );
      } else {
        return props.suggestions.find(
          (suggestion) => suggestion.finding.feature.id === qf.feature.id,
        );
      }
    },
    [panel, props.suggestions, section],
  );

  const toFindingWithSuggestions = useCallback(
      (finding: QualifiedFeature) => ({
        finding: finding,
        suggestion: getSuggestion(finding),
      }),
    [getSuggestion],
  );

  const findingsWithSuggestions = useMemo(() => {
    return sortBy(filteredFindings.map(toFindingWithSuggestions), "suggestion");
  }, [filteredFindings, toFindingWithSuggestions]);

  const onChangeCase = useCallback(
    (qf: QualifiedFeature, r: DataInput, section: PatientCaseGrouping) => {
      if (panelContext.panelCategory.id === KahunPanelSubGroup.Procedures) {
        let first = clone(panel.findings[0]);
        first.qualifiers = [];
        if (getCaseValue(first, section) !== null) {
          setCaseValue(first, null, section, panelContext, false);
        }
      } else if (
        panelContext.panelCategory.id === KahunPanelSubGroup.Medication
      ) {
        setCaseValue(props.drugFeature, null, section, panelContext);
      }

      setCaseValue(qf, r, section, panelContext);
    },
    [
      panelContext,
      panel.findings,
      getCaseValue,
      setCaseValue,
      props.drugFeature,
    ],
  );

  const handleShowMoreClicked = useCallback(() => {
    setMaxItemsPerPanel((current) => current + ITEMS_PAGINATION);
  }, [setMaxItemsPerPanel]);

  useEffect(() => {
    setMaxItemsPerPanel(ITEMS_PAGINATION);
  }, [panelContext, setMaxItemsPerPanel]);

  return (
    <DataInputPanelEditView key={panel?.id || "data-input-panel"}>
      <List>
        <PanelItemHeader
          key={"header"}
          onMakeNormal={onMakeNormal}
          onSelectAbnormal={onSelectAbnormal}
          isProcedurePanelAbnormal={isProcedurePanelAbnormal}
          getCaseValue={getCaseValue}
          section={section}
          usesLevel={usesLevel}
          panel={panel}
          panelContext={panelContext}
        />
        <FindingsList wrap={"false"} enabled={"true"}>
          {findingsWithSuggestions
            .slice(0, maxItemsPerPanel)
            .map((f, index) => (
              <EditablePanelItem
                isThreeway={false}
                className={`${index}-${f.finding?.id}`}
                key={`${index}-${f.finding?.id}`}
                qualifiedFeature={f.finding}
                suggestion={f.suggestion}
                onRefine={createRefineFindingHandler}
                getCaseValue={getCaseValue}
                setCaseValue={onChangeCase}
                panelContext={panelContext}
              />
            ))}
        </FindingsList>
        {findingsWithSuggestions.length > maxItemsPerPanel && (
          <ShowMoreButton onClick={handleShowMoreClicked}>
            {`Show ${Math.min(
              ITEMS_PAGINATION,
              findingsWithSuggestions.length - maxItemsPerPanel,
            )} More`}
          </ShowMoreButton>
        )}
      </List>
    </DataInputPanelEditView>
  );
};
